import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, setPageMeta } from "@/lib/api";
import { trackPurchase } from "@/lib/analytics";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    setPageMeta("Order Confirmed | Ninja Run Club", "Your Ninja Run Club order.");
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setOrder(data);
          setState("paid");
          trackPurchase({
            sessionId, value: (data.amount || 0) / 100, currency: data.currency,
            productName: data.product_name, lookupKey: data.lookup_key,
            size: data.size, quantity: data.quantity,
          });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        setState("error");
        return;
      }
      if (attempts.current >= 8) {
        setState("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="bg-night min-h-[75vh] flex items-center justify-center px-5">
      {state === "checking" && (
        <div className="text-center" data-testid="payment-checking">
          <Loader2 size={28} className="animate-spin mx-auto text-white/60" />
          <p className="font-mono-label text-white/50 mt-5">CONFIRMING YOUR PAYMENT...</p>
        </div>
      )}
      {state === "paid" && (
        <div className="border border-white/20 p-10 lg:p-14 max-w-lg w-full" data-testid="payment-success">
          <CheckCircle2 size={30} className="text-white" />
          <h1 className="font-display text-4xl lg:text-5xl mt-5">ORDER IN.</h1>
          <p className="text-white/60 text-sm mt-4">
            {order?.product_name ? `${order.product_name}${order.size ? ` · size ${order.size}` : ""} is yours.` : "Your order is confirmed."}{" "}
            A receipt is on its way to your inbox. Wear it on the next run.
          </p>
          {order?.order_ref && (
            <p data-testid="payment-order-ref" className="font-mono-label text-white/80 border border-white/25 px-4 py-3 mt-5 inline-block">
              ORDER {order.order_ref} — SAVE THIS NUMBER TO <Link to="/order-lookup" className="underline underline-offset-4">TRACK YOUR ORDER</Link>
            </p>
          )}
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/shop" data-testid="payment-success-shop-link" className="btn-invert bg-white text-ink font-mono-label px-6 py-3.5 border border-white hover:bg-transparent hover:text-white">
              BACK TO THE SHOP
            </Link>
            <Link to="/clubs" data-testid="payment-success-clubs-link" className="btn-invert border border-white/40 font-mono-label px-6 py-3.5 hover:bg-white hover:text-ink">
              FIND YOUR CREW
            </Link>
          </div>
        </div>
      )}
      {state === "timeout" && (
        <div className="border border-white/20 p-10 max-w-lg w-full text-center" data-testid="payment-timeout">
          <p className="font-display text-3xl">STILL PROCESSING.</p>
          <p className="text-white/60 text-sm mt-4">Your payment is being confirmed. If you were charged, your order is safe — check your email for the receipt.</p>
          <Link to="/shop" className="font-mono-label link-underline text-white/60 mt-6 inline-block">BACK TO THE SHOP</Link>
        </div>
      )}
      {state === "error" && (
        <div className="border border-white/20 p-10 max-w-lg w-full text-center" data-testid="payment-error">
          <p className="font-display text-3xl">SOMETHING BROKE.</p>
          <p className="text-white/60 text-sm mt-4">We could not confirm this payment. If you were charged, contact us and we will make it right.</p>
          <Link to="/shop" className="font-mono-label link-underline text-white/60 mt-6 inline-block">BACK TO THE SHOP</Link>
        </div>
      )}
    </div>
  );
}
